import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TrainerSidebar from '../../components/ui/TrainerSidebar';
import SessionMonitorHeader from '../../components/ui/SessionMonitorHeader';
import ActiveSessionCard from './components/ActiveSessionCard';
import SessionOverview from './components/SessionOverview';
import QuickActions from './components/QuickActions';
import AlertsPanel from './components/AlertsPanel';
import SessionFilters from './components/SessionFilters';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';



const TrainerDashboard = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [currentFilters, setCurrentFilters] = useState({
    status: 'all',
    search: '',
    sortBy: 'name'
  });

  // Mock active sessions data
  const [activeSessions] = useState([
    {
      id: 1,
      clientName: 'Margaret Chen',
      clientId: 'MC001',
      currentExercise: 'Seated Leg Extensions',
      currentSet: 2,
      totalSets: 3,
      currentIntensity: 'Level 4',
      targetReps: 12,
      lastCompletedReps: 10,
      completedExercises: 3,
      totalExercises: 8,
      duration: 1254,
      status: 'active',
      alerts: [
        { type: 'info', message: 'Heart rate slightly elevated', priority: 'medium' }
      ]
    },
    {
      id: 2,
      clientName: 'Robert Williams',
      clientId: 'RW002',
      currentExercise: 'Arm Circles',
      currentSet: 1,
      totalSets: 3,
      currentIntensity: 'Level 2',
      targetReps: 15,
      lastCompletedReps: 15,
      completedExercises: 1,
      totalExercises: 6,
      duration: 522,
      status: 'rest',
      alerts: []
    },
    {
      id: 3,
      clientName: 'Dorothy Martinez',
      clientId: 'DM003',
      currentExercise: 'Balance Training',
      currentSet: 3,
      totalSets: 3,
      currentIntensity: 'Level 3',
      targetReps: 10,
      lastCompletedReps: 8,
      completedExercises: 5,
      totalExercises: 7,
      duration: 1945,
      status: 'active',
      alerts: []
    },
    {
      id: 4,
      clientName: 'James Thompson',
      clientId: 'JT004',
      currentExercise: 'Seated Row',
      currentSet: 2,
      totalSets: 3,
      currentIntensity: 'Level 5',
      targetReps: 8,
      lastCompletedReps: 12,
      completedExercises: 2,
      totalExercises: 8,
      duration: 876,
      status: 'paused',
      alerts: [
        { type: 'warning', message: 'Session paused for 5+ minutes', priority: 'medium' }
      ]
    },
    {
      id: 5,
      clientName: 'Linda Davis',
      clientId: 'LD005',
      currentExercise: 'Chest Press',
      currentSet: 3,
      totalSets: 3,
      currentIntensity: 'Level 3',
      targetReps: 10,
      lastCompletedReps: 11,
      completedExercises: 6,
      totalExercises: 6,
      duration: 2134,
      status: 'completed',
      alerts: []
    },
    {
      id: 6,
      clientName: 'Michael Brown',
      clientId: 'MB006',
      currentExercise: 'Leg Press',
      currentSet: 1,
      totalSets: 3,
      currentIntensity: 'Level 6',
      targetReps: 6,
      lastCompletedReps: null,
      completedExercises: 0,
      totalExercises: 9,
      duration: 145,
      status: 'active',
      alerts: []
    }
  ]);

  // Mock alerts data
  const [alerts] = useState([
    {
      id: 1,
      type: 'warning',
      priority: 'high',
      title: 'Heart Rate Alert',
      message: 'Margaret Chen - Heart rate elevated above target zone',
      clientName: 'Margaret Chen',
      timestamp: '2 minutes ago',
      details: `Client's heart rate has been consistently above 85% of maximum for the past 3 minutes during Seated Leg Extensions exercise.`,
      recommendations: [
        'Consider reducing exercise intensity',
        'Extend rest period between sets',
        'Monitor closely for next 10 minutes'
      ]
    },
    {
      id: 2,
      type: 'info',
      priority: 'medium',
      title: 'Session Ending Soon',
      message: 'Robert Williams - Session ending in 5 minutes',
      clientName: 'Robert Williams',
      timestamp: '1 minute ago',
      details: 'Client has 2 exercises remaining with 5 minutes left in scheduled session time.'
    },
    {
      id: 3,
      type: 'warning',
      priority: 'medium',
      title: 'Extended Pause',
      message: 'James Thompson - Session paused for 5+ minutes',
      clientName: 'James Thompson',
      timestamp: '5 minutes ago',
      details: 'Client paused session during Seated Row exercise. May need assistance or check-in.'
    }
  ]);

  useEffect(() => {
    applyFilters();
  }, [activeSessions, currentFilters]);

  const applyFilters = () => {
    let filtered = [...activeSessions];

    // Status filter
    if (currentFilters?.status !== 'all') {
      filtered = filtered?.filter(session => session?.status === currentFilters?.status);
    }

    // Search filter
    if (currentFilters?.search) {
      const searchTerm = currentFilters?.search?.toLowerCase();
      filtered = filtered?.filter(session => 
        session?.clientName?.toLowerCase()?.includes(searchTerm) ||
        session?.currentExercise?.toLowerCase()?.includes(searchTerm) ||
        session?.clientId?.toLowerCase()?.includes(searchTerm)
      );
    }

    // Sort
    filtered?.sort((a, b) => {
      switch (currentFilters?.sortBy) {
        case 'name':
          return a?.clientName?.localeCompare(b?.clientName);
        case 'duration':
          return b?.duration - a?.duration;
        case 'progress':
          const progressA = (a?.completedExercises / a?.totalExercises) * 100;
          const progressB = (b?.completedExercises / b?.totalExercises) * 100;
          return progressB - progressA;
        case 'status':
          const statusOrder = { active: 0, rest: 1, paused: 2, completed: 3 };
          return statusOrder?.[a?.status] - statusOrder?.[b?.status];
        default:
          return 0;
      }
    });

    setFilteredSessions(filtered);
  };

  const handleFilterChange = (filters) => {
    setCurrentFilters(filters);
  };

  const handleIntensityAdjust = (sessionId, currentIntensity) => {
    console.log(`Adjusting intensity for session ${sessionId}, current: ${currentIntensity}`);
    // Navigate to intensity adjustment interface or show modal
  };

  const handleSessionMonitor = (sessionId) => {
    navigate(`/exercise-execution?session=${sessionId}&monitor=true`);
  };

  const handleEmergencyStop = () => {
    const confirmed = window.confirm(
      'This will immediately stop ALL active training sessions and alert emergency contacts. Are you sure?'
    );
    if (confirmed) {
      console.log('Emergency stop activated for all sessions');
      // Implement emergency stop logic
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <TrainerSidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={toggleSidebar}
      />
      {/* Header */}
      <SessionMonitorHeader sidebarCollapsed={sidebarCollapsed} />
      {/* Main Content */}
      <main 
        className={`
          transition-all duration-300 ease-out pt-16
          ${sidebarCollapsed ? 'ml-20' : 'ml-80'}
          lg:${sidebarCollapsed ? 'ml-20' : 'ml-80'}
          md:${sidebarCollapsed ? 'ml-16' : 'ml-72'}
          sm:${sidebarCollapsed ? 'ml-16' : 'ml-64'}
        `}
      >
        <div className="p-6 space-y-6">
          {/* Top Row - Overview and Quick Actions */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <SessionOverview sessions={activeSessions} />
            </div>
            <div>
              <QuickActions onEmergencyStop={handleEmergencyStop} />
            </div>
          </div>

          {/* Middle Row - Filters and Alerts */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            <div>
              <SessionFilters 
                onFilterChange={handleFilterChange}
                sessionCount={activeSessions?.length}
              />
            </div>
            <div className="xl:col-span-3">
              <AlertsPanel alerts={alerts} />
            </div>
          </div>

          {/* Active Sessions Grid */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Active Training Sessions</h2>
                <p className="text-muted-foreground">
                  {filteredSessions?.length} of {activeSessions?.length} sessions displayed
                </p>
              </div>
              <div className="text-sm text-muted-foreground">
                Last updated: {new Date()?.toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </div>
            </div>

            {filteredSessions?.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredSessions?.map((session) => (
                  <ActiveSessionCard
                    key={session?.id}
                    session={session}
                    onIntensityAdjust={handleIntensityAdjust}
                    onSessionMonitor={handleSessionMonitor}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-surface border border-border rounded-xl p-12 text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Search" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No Sessions Found</h3>
                <p className="text-muted-foreground mb-4">
                  No training sessions match your current filters.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setCurrentFilters({ status: 'all', search: '', sortBy: 'name' })}
                  className="breathing-focus"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default TrainerDashboard;